import type { ReactNode } from 'react';
import { Tooltip as TooltipRoot, TooltipContent, TooltipProvider, TooltipTrigger } from '../primitives/tooltip.tsx';
import { Kbd } from './Kbd.tsx';

/** One per app, at the root: opens after 400 ms on hover, at once on focus, with a 300 ms skip delay. */
export { TooltipProvider };

export interface TooltipProps {
  content: ReactNode;
  /** Shortcut shown beside the content, for example ['g', 's'] with `shortcutThen`. */
  shortcut?: string[];
  shortcutThen?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  delay?: number;
  /** When false only the child renders (for example when the label is already visible). */
  enabled?: boolean;
  children: ReactNode;
}

/**
 * A short text label on hover or focus, on the shadcn Tooltip. The child must be focusable and
 * accept a ref (all primitives do). Text only, never on a disabled element.
 */
export function Tooltip({ content, shortcut, shortcutThen, side = 'top', align = 'center', delay, enabled = true, children }: TooltipProps) {
  if (!enabled || content == null || content === '') return <>{children}</>;
  return (
    <TooltipRoot delayDuration={delay}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} align={align} collisionPadding={8}>
        <span>{content}</span>
        {shortcut && <Kbd keys={shortcut} then={shortcutThen} />}
      </TooltipContent>
    </TooltipRoot>
  );
}
